import { useCallback, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { createCartClient, getCartSessionId } from "@/lib/cartSupabase";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CartItem } from "@/hooks/cart/types";
import { calculateCartTotals, type ShippingConfig } from "@/hooks/cart/cartTotals";

const cartQueryKey = (sessionId: string) => ["cart", sessionId] as const;

async function fetchCartItems(
  cartClient: ReturnType<typeof createCartClient>,
  sessionId: string
): Promise<CartItem[]> {
  const { data, error } = await cartClient
    .from("cart_items")
    .select(
      `
        id,
        product_id,
        quantity,
        product:products (
          title,
          price,
          short_description,
          stock_quantity
        )
      `
    )
    .eq("session_id", sessionId);

  if (error) throw error;
  return (data as any) || [];
}

export function useCart(shippingConfig?: ShippingConfig) {
  const queryClient = useQueryClient();
  const sessionId = useMemo(() => getCartSessionId(), []);
  const cartClient = useMemo(() => createCartClient(sessionId), [sessionId]);
  const key = cartQueryKey(sessionId);

  const cartQuery = useQuery({
    queryKey: key,
    queryFn: () => fetchCartItems(cartClient, sessionId),
  });

  const cartItems = (cartQuery.data ?? []) as CartItem[];
  const loading = cartQuery.isLoading;

  // Auto-cleanup inactive carts every minute (uses main supabase client, no session needed)
  useEffect(() => {
    const cleanupInterval = setInterval(async () => {
      try {
        await supabase.rpc("cleanup_old_cart_items");
      } catch (error) {
        console.error("Error cleaning up cart:", error);
      }
    }, 60000); // Every minute

    return () => clearInterval(cleanupInterval);
  }, []);

  const addToCartMutation = useMutation({
    mutationFn: async ({ productId, quantity }: { productId: string; quantity: number }) => {
      const { data: product, error: productError } = await supabase
        .from("products")
        .select("stock_quantity, title, price, short_description")
        .eq("id", productId)
        .single();
      if (productError) throw productError;

      const current = (queryClient.getQueryData<CartItem[]>(key) ?? []) as CartItem[];
      const existingItem = current.find((i) => i.product_id === productId);
      const currentCartQuantity = existingItem ? existingItem.quantity : 0;
      const newTotalQuantity = currentCartQuantity + quantity;

      if (product.stock_quantity !== null) {
        if (product.stock_quantity <= 0) {
          return { skip: true as const, reason: "out_of_stock" as const };
        }
        if (newTotalQuantity > product.stock_quantity) {
          return {
            skip: true as const,
            reason: "insufficient_stock" as const,
            available: product.stock_quantity,
            inCart: currentCartQuantity,
          };
        }
      }

      if (existingItem) {
        const { error } = await cartClient
          .from("cart_items")
          .update({ quantity: newTotalQuantity, updated_at: new Date().toISOString() })
          .eq("id", existingItem.id);
        if (error) throw error;
        return {
          skip: false as const,
          action: "update" as const,
          productId,
          itemId: existingItem.id,
          newTotalQuantity,
          product,
        };
      }

      const { data: insertedItem, error } = await cartClient
        .from("cart_items")
        .insert({ session_id: sessionId, product_id: productId, quantity })
        .select("id")
        .single();
      if (error) throw error;

      return {
        skip: false as const,
        action: "insert" as const,
        productId,
        itemId: insertedItem.id,
        newTotalQuantity: quantity,
        product,
      };
    },
    onSuccess: (result) => {
      if (result.skip) {
        if (result.reason === "out_of_stock") toast.error("Ce produit est en rupture de stock");
        else {
          toast.error(`Stock insuffisant. Disponible: ${result.available}, Dans le panier: ${result.inCart}`);
        }
        return;
      }

      queryClient.setQueryData<CartItem[]>(key, (old = []) => {
        const prev = old as CartItem[];
        if (result.action === "update") {
          return prev.map((it) =>
            it.id === result.itemId ? { ...it, quantity: result.newTotalQuantity } : it
          );
        }
        const newItem: CartItem = {
          id: result.itemId,
          product_id: result.productId,
          quantity: result.newTotalQuantity,
          product: {
            title: result.product.title,
            price: result.product.price,
            short_description: result.product.short_description,
            stock_quantity: result.product.stock_quantity,
          },
        };
        return [...prev, newItem];
      });

      // Keep DB as source of truth (background sync)
      queryClient.invalidateQueries({ queryKey: key });
      toast.success("Produit ajouté au panier");
    },
    onError: async (error) => {
      console.error("Error adding to cart:", error);
      await queryClient.invalidateQueries({ queryKey: key });
      toast.error("Erreur lors de l'ajout au panier");
    },
  });

  const addToCart = useCallback(
    async (productId: string, quantity: number = 1) => {
      await addToCartMutation.mutateAsync({ productId, quantity });
    },
    [addToCartMutation]
  );

  // Update cart item quantity
  const updateQuantityMutation = useMutation({
    mutationFn: async ({ itemId, quantity }: { itemId: string; quantity: number }) => {
      const current = (queryClient.getQueryData<CartItem[]>(key) ?? []) as CartItem[];
      const cartItem = current.find((i) => i.id === itemId);
      if (!cartItem) throw new Error("Cart item not found");

      const { data: product, error: productError } = await supabase
        .from("products")
        .select("stock_quantity")
        .eq("id", cartItem.product_id)
        .single();
      if (productError) throw productError;

      if (product.stock_quantity !== null && quantity > product.stock_quantity) {
        return { skip: true as const, available: product.stock_quantity };
      }

      const { error } = await cartClient
        .from("cart_items")
        .update({ quantity, updated_at: new Date().toISOString() })
        .eq("id", itemId);
      if (error) throw error;

      return { skip: false as const, itemId, quantity };
    },
    onSuccess: (result) => {
      if (result.skip) {
        toast.error(`Stock insuffisant. Disponible: ${result.available}`);
        return;
      }
      queryClient.setQueryData<CartItem[]>(key, (old = []) =>
        (old as CartItem[]).map((it) => (it.id === result.itemId ? { ...it, quantity: result.quantity } : it))
      );
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (error) => {
      console.error("Error updating quantity:", error);
      toast.error("Erreur lors de la mise à jour");
    },
  });

  const removeFromCartMutation = useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      const { error } = await cartClient.from("cart_items").delete().eq("id", itemId);
      if (error) throw error;
      return { itemId };
    },
    onSuccess: ({ itemId }) => {
      queryClient.setQueryData<CartItem[]>(key, (old = []) => (old as CartItem[]).filter((it) => it.id !== itemId));
      queryClient.invalidateQueries({ queryKey: key });
      toast.success("Produit retiré du panier");
    },
    onError: (error) => {
      console.error("Error removing from cart:", error);
      toast.error("Erreur lors de la suppression");
    },
  });

  const clearCartMutation = useMutation({
    mutationFn: async () => {
      const { error } = await cartClient.from("cart_items").delete().eq("session_id", sessionId);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.setQueryData<CartItem[]>(key, []);
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (error) => {
      console.error("Error clearing cart:", error);
      toast.error("Erreur lors du vidage du panier");
    },
  });

  const updateQuantity = useCallback(
    async (itemId: string, quantity: number) => {
      if (quantity <= 0) {
        await removeFromCartMutation.mutateAsync({ itemId });
        return;
      }
      await updateQuantityMutation.mutateAsync({ itemId, quantity });
    },
    [updateQuantityMutation, removeFromCartMutation]
  );

  // Remove item from cart
  const removeFromCart = useCallback(
    async (itemId: string) => {
      await removeFromCartMutation.mutateAsync({ itemId });
    },
    [removeFromCartMutation]
  );

  // Clear entire cart
  const clearCart = useCallback(async () => {
    await clearCartMutation.mutateAsync();
  }, [clearCartMutation]);

  const refreshCart = useCallback(async () => {
    await cartQuery.refetch();
  }, [cartQuery]);

  const totals = useMemo(() => calculateCartTotals(cartItems, shippingConfig), [cartItems, shippingConfig]);

  return {
    cartItems,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    refreshCart,
    ...totals,
  };
}
